import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ListChecks, Check, RotateCcw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  emojiFor, statusLabel, expiraTimer, timeAgo,
} from '../utils/solicitacoesHelpers';

export default function MinhasSolicitacoes() {
  const { user, openLoginModal } = useAuth() || {};
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmResolver, setConfirmResolver] = useState(null);

  useEffect(() => {
    if (!user) { if (openLoginModal) openLoginModal(); return; }
    setLoading(true);
    api.get('/v2/solicitacoes/minhas')
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user, openLoginModal]);

  async function resolver(id) {
    try {
      await api.post(`/v2/solicitacoes/${id}/resolver`);
      setItems((prev) => prev.map((s) => (s.id === id ? { ...s, status: 'resolvida', resolvida_em: new Date().toISOString() } : s)));
      toast.success('Solicitação marcada como resolvida.');
      const sol = items.find((s) => s.id === id);
      if (sol && window.confirm('Deseja avaliar o profissional que te atendeu?')) {
        navigate(`/busca?categoria=${encodeURIComponent(sol.categoria || '')}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao resolver');
    } finally {
      setConfirmResolver(null);
    }
  }

  async function cancelar(id) {
    if (!window.confirm('Cancelar esta solicitação?')) return;
    const snap = items;
    setItems((prev) => prev.map((s) => (s.id === id ? { ...s, status: 'cancelada' } : s)));
    try {
      await api.delete(`/v2/solicitacoes/${id}`);
      toast.success('Solicitação cancelada.');
    } catch (e) {
      setItems(snap);
      toast.error(e?.response?.data?.detail || 'Erro ao cancelar');
    }
  }

  function abrirSimilar(categoria) {
    navigate(`/precisar-de?categoria=${encodeURIComponent(categoria || '')}`);
  }

  if (loading) return <div className="p-12 text-center text-gray-500">Carregando…</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="minhas-solicitacoes-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-purple-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <header className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ListChecks className="w-6 h-6 text-purple-600" /> Minhas solicitações
            </h1>
            <p className="text-sm text-gray-500 mt-1">Histórico dos últimos 30 dias</p>
          </div>
          <Link
            to="/precisar-de"
            data-testid="minhas-nova-btn"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition"
          >
            Nova solicitação
          </Link>
        </header>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center" data-testid="minhas-vazio">
            <p className="text-gray-700 font-medium">Você ainda não fez nenhuma solicitação.</p>
            <p className="text-sm text-gray-500 mt-1">Que tal abrir uma agora?</p>
            <Button asChild className="mt-4"><Link to="/precisar-de">Preciso de…</Link></Button>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((s) => {
              const st = statusLabel(s.status);
              const t = expiraTimer(s.expira_em);
              return (
                <div
                  key={s.id}
                  data-testid={`minha-sol-${s.id}`}
                  className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row gap-3"
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="text-2xl">{emojiFor(s.categoria)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{s.categoria}</span>
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${st.cor}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {st.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{s.texto}</p>
                      {s.palavra_secreta && s.status === 'aberta' && (
                        <div
                          className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200"
                          data-testid={`palavra-secreta-${s.id}`}
                        >
                          <span className="text-[10px] uppercase font-semibold text-amber-700 tracking-wider">Palavra de verificação</span>
                          <span className="text-sm font-mono font-bold text-amber-900">{s.palavra_secreta}</span>
                        </div>
                      )}
                      <div className="text-[11px] text-gray-500 mt-1.5 flex flex-wrap items-center gap-2">
                        <span>Criada {timeAgo(s.criado_em)}</span>
                        {s.status === 'aberta' && <span className={t.cor}>· {t.label}</span>}
                        {s.status === 'resolvida' && s.resolvida_em && (
                          <span>· Resolvida {timeAgo(s.resolvida_em)}</span>
                        )}
                        {typeof s.total_alertas_enviados === 'number' && s.total_alertas_enviados > 0 && (
                          <span>· {s.total_alertas_enviados} {s.total_alertas_enviados === 1 ? 'profissional alertado' : 'profissionais alertados'}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex sm:flex-col gap-2 sm:items-end shrink-0">
                    {s.status === 'aberta' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => setConfirmResolver(s)}
                          data-testid={`minha-sol-${s.id}-resolver`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Resolvida
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelar(s.id)}
                          data-testid={`minha-sol-${s.id}-cancelar`}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                    {s.status === 'expirada' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => abrirSimilar(s.categoria)}
                        data-testid={`minha-sol-${s.id}-similar`}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" /> Abrir similar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!confirmResolver} onOpenChange={(o) => !o && setConfirmResolver(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Você encontrou quem precisava?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Marcar como resolvida ajuda outros usuários e melhora a sua experiência futura.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmResolver(null)}>Ainda não</Button>
            <Button onClick={() => confirmResolver && resolver(confirmResolver.id)} data-testid="minha-sol-confirm-resolver">
              <Check className="w-4 h-4 mr-1" /> Sim, marcar como resolvida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
